import { EntityLike } from '@entities/index';
import { difference, equalsIgnoreOrder, uniqueElements } from '@utils/array';
import { objectEquals } from '@utils/object';
import { WizardBasket, WizardBasketEntityKey } from '@wizard/models';

export function basketsAreDifferent(basket: WizardBasket, targetBasket: WizardBasket): boolean | WizardBasketEntityKey {
  if (!basket && targetBasket) { return true; }
  if (basket && !targetBasket) { return true; }

  const keys = uniqueElements([...Object.keys(basket), ...Object.keys(targetBasket)]);

  if (keys.length !== Object.keys(basket).length) { return true; }
  if (keys.length !== Object.keys(targetBasket).length) { return true; }

  /* check fields, except user and data */
  keys.splice(keys.findIndex(key => key === 'data'), 1);
  keys.splice(keys.findIndex(key => key === 'user'), 1);

  let different: boolean | string = false;
  let key = keys.shift();

  while (key && !different) {
    /* 🪛 check if using raw data is a good idea -> Back object */
    if (key === 'specification') {
      different = JSON.stringify(basket[key] || '') !== JSON.stringify(targetBasket[key] || '');
      if (different) { continue; }
    } else if (!objectEquals(basket[key], targetBasket[key])) {
      different = true;
      continue;
    }
    key = keys.shift();
  }

  if (different) { return key as WizardBasketEntityKey; }

  /* check data now */
  key = 'data';

  if (!basket.data && targetBasket.data) { return key as WizardBasketEntityKey; }
  if (basket.data && !targetBasket.data) { return key as WizardBasketEntityKey; }

  const aData = basket.data;
  const bData = targetBasket.data;

  if (!aData && !bData) { return key as WizardBasketEntityKey; }
  if (aData && !bData) { return key as WizardBasketEntityKey; }

  const aDataKeys = Object.keys(aData);
  const bDataKeys = Object.keys(bData);

  if (aDataKeys.length !== bDataKeys.length) { return key as WizardBasketEntityKey; }

  const dataKeys = uniqueElements([...aDataKeys, ...bDataKeys]);

  key = dataKeys.shift();

  while (key && !different) {
    const aDataValue = aData[key] && Array.from(aData[key] as EntityLike[]);
    const bDataValue = bData[key] && Array.from(bData[key] as EntityLike[]);

    if (!aDataValue && !bDataValue) {
      key = dataKeys.shift();
      continue;
    } else if (!aDataValue && bDataValue) {
      different = true;
      continue;
    } else if (aDataValue && !bDataValue) {
      different = true;
      continue;
    } else if (aDataValue.length !== bDataValue.length) {
      different = true;
      continue;
    }

    /* test ids */
    const aDataValueIds = aDataValue.map(data => data.id);
    const bDataValueIds = aDataValue.map(data => data.id);
    const diffIds = difference(aDataValueIds, bDataValueIds).length > 0;

    if (diffIds) {
      different = true;
      continue;
    }

    /* test Items */
    const itemsKey = 'items';

    for (let obj in aDataValue) {
      const aDataItems = aDataValue[obj][itemsKey] && Array.from(aDataValue[obj][itemsKey] as EntityLike[]);
      const bDataItems = bDataValue[obj][itemsKey] && Array.from(bDataValue[obj][itemsKey] as EntityLike[]);

      if (!aDataItems && !bDataItems) {
        key = dataKeys.shift();
        continue;
      } else if (!aDataItems && bDataItems) {
        different = true;
        key = `${key}.items`;
        continue;
      } else if (aDataItems && !bDataItems) {
        different = true;
        key = `${key}.items`;
        continue;
      } else if (aDataItems.length !== bDataItems.length) {
        different = true;
        key = `${key}.items`;
        continue;
      }

      const diffItems = !equalsIgnoreOrder(aDataValue['items'], bDataValue['items']);
      if (diffItems) {
        different = true;
        key = `${key}.items`;
        continue;
      }

      if (different) { break; }

    }

    if (different) { break; }

    key = dataKeys.shift();
  }

  return (typeof key === 'string' ? (key as WizardBasketEntityKey) : !!key);
}
